import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  PencilSimple,
  Trash,
  Buildings,
  Pencil,
  ArrowSquareOutIcon,
  MapPin,
} from '@phosphor-icons/react';
import Alerts from '../../../shared/components/ui/Alerts';
import GenericTables from '../../../shared/components/ui/GenericTables';
import ActionMenu from '../../../shared/components/ui/ActionMenu';
import PageHeader from '../../../shared/components/ui/PageHeader';
import { companyService } from '../../../services/companies/companyService.js';
import { userService } from '../../../services/users/userService.js';
import { myCompanyService } from '../../../services/companies/myCompanyService.js';
import { getUserFromToken } from '../../../context/authContext.jsx';
import ExpansionPanel from '../components/ExpansionPanel.jsx';
import LocationPickerModal from '../../../shared/components/common/LocationPickerModal.jsx';

const emptyForm = {
  name: '', hasGeoApi: false, notificationData: null, planId: '',
  strategy: '', apiUser: '', apiPassword: '', apiBaseUrl: '',
  baseCoordinates: '',
};

const GEO_FIELDS = [
  { name: 'strategy',    label: 'Estrategia',   type: 'text',     placeholder: 'ej. oauth2' },
  { name: 'apiUser',     label: 'Usuario API',  type: 'text',     placeholder: 'usuario' },
  { name: 'apiPassword', label: 'Contraseña API', type: 'text', placeholder: 'token' },
  { name: 'apiBaseUrl',  label: 'URL Base',     type: 'text',     placeholder: 'https://api.ejemplo.com' },
];

const geoComplete = (d) =>
  GEO_FIELDS.every(f => d[f.name] && d[f.name]?.toString().trim() !== '');

const coordinatePattern = /^\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*,\s*([+-]?(?:\d+(?:\.\d*)?|\.\d+))\s*$/;

const parseBaseCoordinates = (value) => {
  if (!value?.trim()) {
    return { error: 'La latitud y la longitud son obligatorias.' };
  }
  const match = value?.match(coordinatePattern);
  if (!match) return { error: 'Ingresa coordenadas válidas en formato lat, lng.' };

  const baseLat = Number(match[1]);
  const baseLng = Number(match[2]);
  if (!Number.isFinite(baseLat) || !Number.isFinite(baseLng)) {
    return { error: 'Ingresa coordenadas válidas en formato lat, lng.' };
  }
  if (baseLat < -90 || baseLat > 90) {
    return { error: 'La latitud debe estar entre -90 y 90.' };
  }
  if (baseLng < -180 || baseLng > 180) {
    return { error: 'La longitud debe estar entre -180 y 180.' };
  }

  return { coordinates: { lat: baseLat, lng: baseLng } };
};

const roundCoordinate = (value) => Math.round(value * 1000000) / 1000000;

const formatBaseCoordinates = (baseLat, baseLng) =>
  baseLat !== null && baseLat !== undefined && baseLat !== ''
  && baseLng !== null && baseLng !== undefined && baseLng !== ''
    ? `${roundCoordinate(Number(baseLat))}, ${roundCoordinate(Number(baseLng))}`
    : '';

const normalizeBaseCoordinates = (lat, lng) => {
  const coordinates = parseBaseCoordinates(`${lat}, ${lng}`).coordinates;
  return coordinates
    ? { lat: roundCoordinate(coordinates.lat), lng: roundCoordinate(coordinates.lng) }
    : null;
};

/* ── FormFields fuera del componente para evitar remount en cada keystroke ── */
const FormFields = ({
  data,
  onChange,
  onBaseCoordinatesChange,
  onOpenBaseMap,
  baseCoordinatesError,
  origin,
  notif,
  plans,
  geoOpen,
  onToggleGeo,
}) => (
  <div className="p-6">
    {notif.origin === origin && notif.show && (
      <div className={`mb-4 p-4 rounded-lg ${
        notif.type === 'success'
          ? 'bg-success/12 border border-success text-success'
          : 'bg-error/12 border border-error text-error'
      }`}>
        {notif.message}
      </div>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
      {/* Name */}
      <div className="sm:col-span-2">
        <label className="block text-xs sm:text-sm font-medium text-content-muted mb-1">Nombre de la empresa</label>
        <input
          type="text"
          name="name"
          autoComplete="off"
          placeholder="Nombre de la empresa"
          value={data.name ?? ''}
          onChange={e => onChange(e, origin)}
          className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
          maxLength={220}
        />
      </div>

      {/* Plan */}
      <div className="sm:col-span-2">
        <label className="block text-xs sm:text-sm font-medium text-content-muted mb-1">Plan</label>
        <select
          name="planId"
          value={data.planId ?? ''}
          onChange={e => onChange(e, origin)}
          className="w-full px-3 py-2 rounded-md bg-surface-2 text-content focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
        >
          <option value="">Selecciona un plan</option>
          {plans.map(p => (
            <option key={p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
      </div>

      <div className="sm:col-span-2">
        <label className="block text-xs sm:text-sm font-medium text-content-muted mb-1">Base operativa</label>
        <div className="flex gap-4 items-start">
          <div className="flex-1">
            <input
              type="text"
              name="baseCoordinates"
              autoComplete="off"
              placeholder="Ej: -31.5375, -68.5364"
              value={data.baseCoordinates ?? ''}
              onChange={e => onBaseCoordinatesChange(e, origin)}
              className={`w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border ${baseCoordinatesError ? 'border-error' : 'border-line'}`}
            />
            {baseCoordinatesError && (
              <p className="text-xs sm:text-sm font-medium text-error mt-1">{baseCoordinatesError}</p>
            )}
          </div>
          <button
            type="button"
            onClick={() => onOpenBaseMap(origin)}
            className="h-12 px-4 py-2 bg-amber hover:bg-amber-hover text-on-amber rounded-md transition-colors flex items-center justify-center"
            title="Seleccionar coordenadas en el mapa"
            aria-label="Seleccionar base operativa en el mapa"
          >
            <MapPin size={16} />
          </button>
        </div>
      </div>
    </div>

    <ExpansionPanel
      title="Api geolocalización"
      open={geoOpen}
      onToggle={onToggleGeo}
      dot={data.hasGeoApi ? 'bg-success' : 'bg-muted-deep'}
      badge={data.hasGeoApi}
      className="mb-2"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {GEO_FIELDS.map(f => (
          <div key={f.name}>
            <label className="block text-xs sm:text-sm font-medium text-content-muted mb-1">{f.label}</label>
            <input
              type={f.type}
              name={f.name}
              autoComplete="off"
              placeholder={f.placeholder}
              value={data[f.name] ?? ''}
              onChange={e => onChange(e, origin)}
              className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line"
            />
          </div>
        ))}
      </div>
    </ExpansionPanel>
  </div>
);

const CompanyManagement = ({ onCompanyChanged } = {}) => {
  /* ── data ─────────────────────────────────────────────── */
  const [companies,   setCompanies]   = useState([]);
  const [plans,       setPlans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  /* ── pagination ──────────────────────────────────────── */
  const [pageSize,    setPageSize]    = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems,  setTotalItems]  = useState(0);
  const [totalPages,  setTotalPages]  = useState(1);

  /* ── modals ──────────────────────────────────────────── */
  const [showModal,     setShowModal]     = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [newCompany,    setNewCompany]    = useState(emptyForm);
  const [editCompany,   setEditCompany]   = useState(null);
  const [geoOpenCreate, setGeoOpenCreate] = useState(false);
  const [geoOpenEdit,   setGeoOpenEdit]   = useState(false);
  const [baseCoordinatesError, setBaseCoordinatesError] = useState({ create: '', edit: '' });
  const [baseMapOpen, setBaseMapOpen] = useState(false);
  const [baseMapOrigin, setBaseMapOrigin] = useState('create');
  const [baseMapCoordinates, setBaseMapCoordinates] = useState({ lat: '', lng: '' });

  /* ── delete confirm ──────────────────────────────────── */
  const [showAlerts,         setShowAlerts]         = useState(false);
  const [alertsTitle,        setAlertsTitle]        = useState('Aviso');
  const [alertsContent,      setAlertsContent]      = useState('');
  const [deleteTargetId,     setDeleteTargetId]     = useState(null);
  const [accessTargetId,     setAccessTargetId]     = useState(null);
  const [confirmAction,      setConfirmAction]      = useState(null);

  /* ── inline notification ─────────────────────────────── */
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success', origin: '' });

  /* ── fetch ───────────────────────────────────────────── */
  const fetchCompanies = async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const p = Number(page) || 1;
      const s = Number(size) || 10;
      const response = await companyService.getCompanies({ pageSize: s, pageCurrent: p - 1 });

      const list = response.companies ?? [];
      setCompanies(list);

      const pag = response.pagination;
      if (pag) {
        const total = Number(pag.totalItems) || list.length;
        setTotalItems(total);
        setTotalPages(total > 0 ? Math.ceil(total / s) : 1);
      } else {
        setTotalItems(list.length);
        setTotalPages(1);
      }
      setError(null);
    } catch {
      setError('Error al cargar empresas');
      setCompanies([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const result = await companyService.getPlans();
      setPlans(result?.plans ?? []);
    } catch {
      setPlans([]);
    }
  };

  useEffect(() => { fetchCompanies(); }, [currentPage, pageSize]);
  useEffect(() => { fetchPlans(); }, []);

  /* ── notifications ───────────────────────────────────── */
  const showNotification = (message, type = 'success', origin = '') => {
    setNotification({ show: true, message, type, origin });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success', origin: '' }), 3000);
  };

  /* ── input handlers ──────────────────────────────────── */
  const handleInputChange = (e, origin) => {
    const { name, type, value, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    if (origin === 'create') setNewCompany(prev => ({ ...prev, [name]: val }));
    else                     setEditCompany(prev => ({ ...prev, [name]: val }));
  };

  const handleBaseCoordinatesChange = (e, origin) => {
    const value = e.target.value;
    const result = parseBaseCoordinates(value);
    const error = value.trim() && result.error ? result.error : '';

    setBaseCoordinatesError(prev => ({ ...prev, [origin]: error }));
    if (origin === 'create') {
      setNewCompany(prev => ({ ...prev, baseCoordinates: value }));
    } else {
      setEditCompany(prev => ({ ...prev, baseCoordinates: value }));
    }
  };

  const openBaseMap = (origin) => {
    const company = origin === 'create' ? newCompany : editCompany;
    const selected = parseBaseCoordinates(company?.baseCoordinates ?? '').coordinates;
    setBaseMapCoordinates(selected ?? { lat: '', lng: '' });
    setBaseMapOrigin(origin);
    setBaseMapOpen(true);
  };

  const validateBaseCoordinates = (company, origin) => {
    const result = parseBaseCoordinates(company?.baseCoordinates ?? '');
    const error = result.error ?? '';
    setBaseCoordinatesError(prev => ({ ...prev, [origin]: error }));
    return result.coordinates ?? null;
  };

  const buildCompanyPayload = (company, coordinates) => {
    const { id, baseCoordinates, ...rest } = company;
    return {
      ...rest,
      baseLat: roundCoordinate(coordinates.lat),
      baseLng: roundCoordinate(coordinates.lng),
      hasGeoApi: geoComplete(company),
    };
  };

  /* ── CRUD ─────────────────────────────────────────────── */
  const handleAddCompany = async () => {
    if (!newCompany.name.trim()) {
      showNotification('El nombre de la empresa es requerido', 'error', 'create');
      return;
    }
    if (!newCompany.planId) {
      showNotification('Debe seleccionar un plan', 'error', 'create');
      return;
    }
    const coordinates = validateBaseCoordinates(newCompany, 'create');
    if (!coordinates) return;
    try {
      const payload = buildCompanyPayload(newCompany, coordinates);
      await companyService.createCompany(payload);
      setShowModal(false);
      setNewCompany(emptyForm);
      setGeoOpenCreate(false);
      setBaseCoordinatesError(prev => ({ ...prev, create: '' }));
      await fetchCompanies(currentPage, pageSize);
    } catch {
      showNotification('Error al crear la empresa', 'error', 'create');
    }
  };

  const handleEditCompany = async () => {
    if (!editCompany?.name?.trim()) {
      showNotification('El nombre de la empresa es requerido', 'error', 'edit');
      return;
    }
    if (!editCompany.planId) {
      showNotification('Debe seleccionar un plan', 'error', 'edit');
      return;
    }
    const coordinates = validateBaseCoordinates(editCompany, 'edit');
    if (!coordinates) return;
    try {
      const payload = buildCompanyPayload(editCompany, coordinates);
      await companyService.updateCompany(editCompany.id, payload);
      setShowModalEdit(false);
      setEditCompany(null);
      setGeoOpenEdit(false);
      setBaseCoordinatesError(prev => ({ ...prev, edit: '' }));
      await fetchCompanies(currentPage, pageSize);
    } catch {
      showNotification('Error al actualizar la empresa', 'error', 'edit');
    }
  };

  const handleDeleteCompany = async () => {
    try {
      await companyService.deleteCompany(deleteTargetId);
      await fetchCompanies(currentPage, pageSize);
    } catch {
      setError('Error al eliminar empresa');
    }
  };

  const handleAccessCompany = async () => {
    try {
      const currentUser = getUserFromToken();
      if (!currentUser?.email) return;
      await userService.changeCompany(accessTargetId);

      const { company } = await myCompanyService.getMyCompany();
      const storedUser = JSON.parse(localStorage.getItem('userData') || '{}');
      localStorage.setItem('userData', JSON.stringify({ ...storedUser, companyName: company.name }));
      onCompanyChanged?.(company.name);
    } catch {
      showNotification('Error al acceder a la empresa', 'error', '');
    }
  };

  const handleConfirmAlert = () => {
    if (confirmAction === 'delete') handleDeleteCompany();
    else if (confirmAction === 'access') handleAccessCompany();
    setShowAlerts(false);
    setConfirmAction(null);
  };

  /* ── modal helpers ───────────────────────────────────── */
  const openModal = () => {
    setNewCompany(emptyForm);
    setGeoOpenCreate(false);
    setBaseCoordinatesError(prev => ({ ...prev, create: '' }));
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setNewCompany(emptyForm);
    setGeoOpenCreate(false);
    setBaseMapOpen(false);
    setBaseCoordinatesError(prev => ({ ...prev, create: '' }));
  };

  const openModalEdit = (row) => {
    const hasGeo = row.hasGeoApi;
    setEditCompany({
      ...row,
      baseCoordinates: formatBaseCoordinates(row.baseLat, row.baseLng),
    });
    setGeoOpenEdit(hasGeo);
    setBaseCoordinatesError(prev => ({ ...prev, edit: '' }));
    setShowModalEdit(true);
  };
  const closeModalEdit = () => {
    setShowModalEdit(false);
    setEditCompany(null);
    setGeoOpenEdit(false);
    setBaseMapOpen(false);
    setBaseCoordinatesError(prev => ({ ...prev, edit: '' }));
  };

  /* lock body scroll when modal open */
  useEffect(() => {
    if (!showModal && !showModalEdit) return undefined;
    const scrollY = window.scrollY;
    document.body.style.overflow  = 'hidden';
    document.body.style.position  = 'fixed';
    document.body.style.top       = `-${scrollY}px`;
    document.body.style.width     = '100%';
    return () => {
      document.body.style.overflow  = '';
      document.body.style.position  = '';
      document.body.style.top       = '';
      document.body.style.width     = '';
      window.scrollTo(0, scrollY);
    };
  }, [showModal, showModalEdit]);

  /* ── portal helper ───────────────────────────────────── */
  const renderModal = (content) =>
    typeof document !== 'undefined'
      ? createPortal(
          <div className="fixed inset-0 z-[10020] bg-[var(--app-overlay)] p-4 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex min-h-full items-start justify-center sm:items-center">
              {content}
            </div>
          </div>,
          document.body
        )
      : null;

  /* ── error state ─────────────────────────────────────── */
  if (error && !loading && companies.length === 0) {
    return (
      <div className="bg-status-error/10 border border-status-error/20 rounded-lg p-4 mb-4">
        <div className="flex items-center space-x-2">
          <span className="text-status-error">⚠</span>
          <span className="text-status-error">{error}</span>
        </div>
      </div>
    );
  }

  /* ── render ───────────────────────────────────────────── */
  return (
    <div>
      <PageHeader
        title="Gestión de Empresas"
        subtitle="Administra las empresas del sistema"
        icon={<Buildings size={28} />}
        actionButton={{
          label: 'Agregar Empresa',
          icon: <Plus size={16} />,
          onClick: openModal,
        }}
      />

      <div className="space-y-6">
        {/* ── Modal crear ──────────────────────────────────── */}
        {showModal && renderModal(
          <div className="bg-surface rounded-xl shadow-xl border border-line w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="bg-bg shadow-sm text-content px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Plus size={16} className="mr-2" />
                  Agregar Nueva Empresa
                </h3>
                <button onClick={closeModal} className="text-content hover:text-content-muted transition-colors">
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>
            <FormFields
              data={newCompany}
              onChange={handleInputChange}
              onBaseCoordinatesChange={handleBaseCoordinatesChange}
              onOpenBaseMap={openBaseMap}
              baseCoordinatesError={baseCoordinatesError.create}
              origin="create"
              notif={notification}
              plans={plans}
              geoOpen={geoOpenCreate}
              onToggleGeo={() => setGeoOpenCreate(v => !v)}
            />
            <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button onClick={closeModal} className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface flex items-center justify-center space-x-2">
                <span>Cancelar</span>
              </button>
              <button onClick={handleAddCompany} className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2">
                <span>Agregar Empresa</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Modal editar ─────────────────────────────────── */}
        {showModalEdit && editCompany && renderModal(
          <div className="bg-surface rounded-xl shadow-xl border border-line w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
            <div className="bg-bg shadow-sm text-content px-6 py-4 rounded-t-xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Pencil size={16} className="mr-2" />
                  Editar Empresa
                </h3>
                <button onClick={closeModalEdit} className="text-content hover:text-content-muted transition-colors">
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>
            <FormFields
              data={editCompany}
              onChange={handleInputChange}
              onBaseCoordinatesChange={handleBaseCoordinatesChange}
              onOpenBaseMap={openBaseMap}
              baseCoordinatesError={baseCoordinatesError.edit}
              origin="edit"
              notif={notification}
              plans={plans}
              geoOpen={geoOpenEdit}
              onToggleGeo={() => setGeoOpenEdit(v => !v)}
            />
            <div className="px-6 pb-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button onClick={closeModalEdit} className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface flex items-center justify-center space-x-2">
                <span>Cancelar</span>
              </button>
              <button onClick={handleEditCompany} className="bg-amber hover:bg-amber-hover text-on-amber px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2">
                <span>Guardar Cambios</span>
              </button>
            </div>
          </div>
        )}

        {/* ── tabla ────────────────────────────────────────── */}
        <LocationPickerModal
          open={baseMapOpen}
          coordinates={baseMapCoordinates}
          onCoordinatesChange={setBaseMapCoordinates}
          onClose={() => setBaseMapOpen(false)}
          onConfirm={(coordinates) => {
            const value = `${coordinates.lat}, ${coordinates.lng}`;
            if (baseMapOrigin === 'create') {
              setNewCompany(prev => ({ ...prev, baseCoordinates: value }));
            } else {
              setEditCompany(prev => ({ ...prev, baseCoordinates: value }));
            }
            setBaseCoordinatesError(prev => ({ ...prev, [baseMapOrigin]: '' }));
            setBaseMapOpen(false);
          }}
          normalizeCoordinates={normalizeBaseCoordinates}
          markerId={`company-${baseMapOrigin}-base-location`}
          minZoom={13}
          maxBounds={[[-90, -180], [90, 180]]}
        />

        <div className="relative task-section md:overflow-hidden md:rounded-xl md:border md:border-line md:bg-surface md:shadow-md">
          <GenericTables
            columns={[
              {
                key: 'name',
                label: 'Empresa',
                align: 'center',
                render: (row) => row.name || 'Sin nombre',
              },
              {
                key: 'planId',
                label: 'Plan',
                align: 'center',
                render: (row) => row.planId,
              },
              {
                key: 'hasGeoApi',
                label: 'GeoAPI',
                align: 'center',
                isChip: true,
                render: (row) => (
                  <span className={`text-xs font-medium px-2 py-1 rounded-full border ${row.hasGeoApi ? 'bg-success/12 text-success border-success' : 'bg-surface-2 text-content-muted border-line'}`}>
                    {row.hasGeoApi ? 'Activa' : 'Inactiva'}
                  </span>
                ),
              },
              {
                key: 'actions',
                label: 'Acciones',
                align: 'center',
                isAction: true,
                render: (row) => (
                  <div onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                      options={[
                        {
                          label: 'Acceder',
                          icon: <ArrowSquareOutIcon size={16} />,
                          onClick: () => {
                            setAccessTargetId(row.id);
                            setConfirmAction('access');
                            setAlertsTitle('Confirmar acceso a empresa');
                            setAlertsContent('¿Estás seguro de que quieres acceder a esta empresa?');
                            setShowAlerts(true);
                          },
                        },
                        {
                          label: 'Editar',
                          icon: <PencilSimple size={16} />,
                          onClick: () => openModalEdit(row),
                        },
                        {
                          label: 'Eliminar',
                          icon: <Trash size={16} />,
                          onClick: () => {
                            setDeleteTargetId(row.id);
                            setConfirmAction('delete');
                            setAlertsTitle('Confirmar Eliminación');
                            setAlertsContent('¿Estás seguro de que quieres eliminar esta empresa?');
                            setShowAlerts(true);
                          },
                        },
                      ]}
                    />
                  </div>
                ),
              },
            ]}
            data={companies}
            showFooter={true}
            pagination={{
              currentPage,
              totalPages,
              pageSize,
              totalItems,
              pageSizeOptions: [5, 10, 20, 50],
              onPageChange: setCurrentPage,
              onPageSizeChange: (newSize) => { setPageSize(newSize); setCurrentPage(1); },
            }}
            onRowClick={(row) => openModalEdit(row)}
          />
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-bg/60">
              <div className="flex items-center gap-3 rounded-lg border border-line bg-surface/90 px-4 py-3 text-sm text-content-muted shadow-lg">
                <span className="h-5 w-5 animate-spin rounded-full border-2 border-amber border-t-transparent" />
                Consultando empresas...
              </div>
            </div>
          )}
        </div>

        {/* ── alert confirmar borrar ────────────────────────── */}
        {showAlerts && (
          <Alerts title={alertsTitle} onClose={() => setShowAlerts(false)} width="max-w-md">
            <h2 className="text-lg font-semibold">{alertsContent}</h2>
            <div className="flex justify-end space-x-3 pt-6 border-t border-line">
              <button
                type="button"
                onClick={() => { setShowAlerts(false); setDeleteTargetId(null); setAccessTargetId(null); setConfirmAction(null); }}
                className="px-4 py-2 text-content-muted bg-surface-2 rounded-md hover:bg-surface"
              >
                Cancelar
              </button>
              <button
                type="submit"
                onClick={handleConfirmAlert}
                className={`px-6 py-2 rounded-md ${
                  confirmAction === 'delete'
                    ? 'bg-error text-white hover:opacity-90'
                    : 'bg-amber text-on-amber hover:bg-amber-hover'
                }`}
              >
                {confirmAction === 'access' ? 'Acceder' : 'Eliminar'}
              </button>
            </div>
          </Alerts>
        )}
      </div>
    </div>
  );
};

export default CompanyManagement;
